import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TweaksProvider } from "./context/TweaksContext";
import { ColorModeProvider } from "./context/ColorModeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  SiteSettingsProvider,
  useSiteSettings,
} from "./context/SiteSettingsContext";
import { Layout } from "./components/Layout";
import { BootSplash } from "./components/BootSplash";
import { HomePage } from "./pages/HomePage";
import { RootLandingPage } from "./pages/RootLandingPage";
import { EventPage } from "./pages/EventPage";
import { DorothyPage } from "./pages/DorothyPage";
import { LivePage } from "./pages/LivePage";
import { GalleryPage } from "./pages/GalleryPage";
import { GalleryAlbumPage } from "./pages/GalleryAlbumPage";
import { VideosPage } from "./pages/VideosPage";
import { VideoWatchPage } from "./pages/VideoWatchPage";
import { PartnerPage } from "./pages/PartnerPage";
import { ContactPage } from "./pages/ContactPage";
import { TestimonialsPage } from "./pages/TestimonialsPage";
import { ParticipatePage } from "./pages/ParticipatePage";
import { GivePage } from "./pages/GivePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { SignInPage } from "./pages/SignInPage";
import { BlogIndexPage } from "./pages/BlogIndexPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { SitePageView } from "./pages/SitePageView";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminTonightPage } from "./pages/admin/AdminTonightPage";
import { AdminContentHubPage } from "./pages/admin/AdminContentHubPage";
import { AdminPeoplePage } from "./pages/admin/AdminPeoplePage";
import { AdminAddressBookPage } from "./pages/admin/AdminAddressBookPage";
import { MemberCheckInPage } from "./pages/MemberCheckInPage";
import { AdminTestimonialsPage } from "./pages/admin/AdminTestimonialsPage";
import { AdminParticipationPage } from "./pages/admin/AdminParticipationPage";
import { AdminRsvpsPage } from "./pages/admin/AdminRsvpsPage";
import { AdminPartnersPage } from "./pages/admin/AdminPartnersPage";
import { AdminContactPage } from "./pages/admin/AdminContactPage";
import { AdminSubscribersPage } from "./pages/admin/AdminSubscribersPage";
import { AdminBlogPage } from "./pages/admin/AdminBlogPage";
import { AdminBlogEditPage } from "./pages/admin/AdminBlogEditPage";
import { AdminContentPage } from "./pages/admin/AdminContentPage";
import { AdminContentJsonPage } from "./pages/admin/AdminContentJsonPage";
import { AdminPageEditPage } from "./pages/admin/AdminPageEditPage";
import { AdminYoutubeLibraryPage } from "./pages/admin/AdminYoutubeLibraryPage";
import { AdminCommentsPage } from "./pages/admin/AdminCommentsPage";
import { AdminSettingsLayout } from "./pages/admin/settings/AdminSettingsLayout";
import { AdminSettingsGeneralPage } from "./pages/admin/settings/AdminSettingsGeneralPage";
import { AdminSettingsContactsPage } from "./pages/admin/settings/AdminSettingsContactsPage";
import { AdminSettingsSocialPage } from "./pages/admin/settings/AdminSettingsSocialPage";
import { AdminSettingsFooterPage } from "./pages/admin/settings/AdminSettingsFooterPage";
import { AdminSettingsEmailPage } from "./pages/admin/settings/AdminSettingsEmailPage";
import { AdminSettingsSmsPage } from "./pages/admin/settings/AdminSettingsSmsPage";
import { AdminSettingsAddressBookPage } from "./pages/admin/settings/AdminSettingsAddressBookPage";
import { AdminSettingsYoutubePage } from "./pages/admin/settings/AdminSettingsYoutubePage";
import { AdminSettingsUsersPage } from "./pages/admin/settings/AdminSettingsUsersPage";
import { AdminEventsListPage } from "./pages/admin/events/AdminEventsListPage";
import { AdminEventNewPage } from "./pages/admin/events/AdminEventNewPage";
import { AdminEventHubLayout } from "./pages/admin/events/AdminEventHubLayout";
import { AdminEventOverviewPage } from "./pages/admin/events/AdminEventOverviewPage";
import { AdminEventStreamPage } from "./pages/admin/events/AdminEventStreamPage";
import { AdminEventGalleryPage } from "./pages/admin/events/AdminEventGalleryPage";
import { AdminEventAttendeesPage } from "./pages/admin/events/AdminEventAttendeesPage";
import { AdminEventRsvpsPage as AdminEventHubRsvpsPage } from "./pages/admin/events/AdminEventRsvpsPage";
import { AdminEventBlogPage } from "./pages/admin/events/AdminEventBlogPage";
import { AdminCheckInPage } from "./pages/admin/AdminCheckInPage";
import { AdminAttendancePage } from "./pages/admin/AdminAttendancePage";
import { AdminSelfCheckInPage } from "./pages/admin/AdminSelfCheckInPage";
import { AdminEventLegacyRedirect } from "./pages/admin/AdminEventLegacyRedirect";
import { AdminCopyPage } from "./pages/admin/AdminCopyPage";
import { AdminCommunicationPage } from "./pages/admin/AdminCommunicationPage";
import { CopyEditProvider } from "./context/CopyEditContext";
import { SiteCopyProvider } from "./context/SiteCopyContext";
import { useComingSoonBoot } from "./hooks/useComingSoonBoot";

export default function App() {
  return (
    <ColorModeProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <TweaksProvider>
            <BrowserRouter>
              <SiteCopyProvider>
                <CopyEditProvider>
                  <SiteRoutes />
                </CopyEditProvider>
              </SiteCopyProvider>
            </BrowserRouter>
          </TweaksProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </ColorModeProvider>
  );
}

function SiteRoutes() {
  const { user, loading: authLoading } = useAuth();
  const { settings, loading: siteLoading } = useSiteSettings();

  if (authLoading || siteLoading) {
    return <BootSplash progress={0.25} />;
  }

  // Anonymous visitors land on coming-soon (event, directions, explore, live)
  // until the full site is opened. When live, `/` is always the event landing
  // (RootLandingPage); marketing HomePage is `/home` only. About / Blog / Gallery /
  // Videos / Participate / Give stay reachable from header, Explore, and footer.
  // Logged-in members (including admins) see the full site.
  const gated = settings.mode === "coming-soon" && !user;

  if (gated) {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route element={<Layout />}>
          <Route path="dorothy" element={<DorothyPage />} />
          <Route path="blog" element={<BlogIndexPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="gallery/:slug" element={<GalleryAlbumPage />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="videos/view/:videoId" element={<VideoWatchPage />} />
          <Route path="participate" element={<ParticipatePage />} />
          <Route path="give" element={<GivePage />} />
        </Route>
        <Route path="*" element={<ComingSoonGate />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<RootLandingPage />} />
        <Route path="home" element={<HomePage />} />
        <Route path="preview/coming-soon" element={<ComingSoonGate />} />
        <Route path="event" element={<EventPage />} />
        <Route path="dorothy" element={<DorothyPage />} />
        <Route path="live" element={<LivePage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="gallery/:slug" element={<GalleryAlbumPage />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="videos/view/:videoId" element={<VideoWatchPage />} />
        <Route path="partner" element={<PartnerPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="testimonials" element={<TestimonialsPage />} />
        <Route path="participate" element={<ParticipatePage />} />
        <Route path="give" element={<GivePage />} />
        <Route path="blog" element={<BlogIndexPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        <Route path="pages/:slug" element={<SitePageView />} />
        <Route path="sign-in" element={<SignInPage />} />
        <Route path="check-in" element={<MemberCheckInPage />} />
        <Route
          path="coming-soon"
          element={<Navigate to="/" replace />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="admin/check-in/self" element={<AdminSelfCheckInPage />} />

      <Route path="admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="tonight" element={<AdminTonightPage />} />
        <Route path="content-hub" element={<AdminContentHubPage />} />
        <Route path="studio" element={<Navigate to="/admin/content-hub" replace />} />
        <Route path="people" element={<AdminPeoplePage />} />
        <Route path="people/address-book" element={<AdminAddressBookPage />} />
        <Route path="address-book" element={<AdminAddressBookPage />} />
        <Route path="testimonials" element={<AdminTestimonialsPage />} />
        <Route path="participation" element={<AdminParticipationPage />} />
        <Route path="rsvps" element={<AdminRsvpsPage />} />
        <Route path="partners" element={<AdminPartnersPage />} />
        <Route path="contact" element={<AdminContactPage />} />
        <Route path="subscribers" element={<AdminSubscribersPage />} />
        <Route path="blog" element={<AdminBlogPage />} />
        <Route path="blog/new" element={<AdminBlogEditPage />} />
        <Route path="blog/:id" element={<AdminBlogEditPage />} />
        <Route path="events/new" element={<AdminEventNewPage />} />
        <Route path="events/:id" element={<AdminEventHubLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<AdminEventOverviewPage />} />
          <Route path="stream" element={<AdminEventStreamPage />} />
          <Route path="gallery" element={<AdminEventGalleryPage />} />
          <Route path="attendees" element={<AdminEventAttendeesPage />} />
          <Route path="rsvps" element={<AdminEventHubRsvpsPage />} />
          <Route path="blog" element={<AdminEventBlogPage />} />
        </Route>
        <Route path="events" element={<AdminEventsListPage />} />
        <Route path="gallery" element={<AdminEventLegacyRedirect target="gallery" />} />
        <Route path="check-in" element={<AdminCheckInPage />} />
        <Route path="attendance" element={<AdminAttendancePage />} />
        <Route path="copy" element={<AdminCopyPage />} />
        <Route path="communication" element={<AdminCommunicationPage />} />
        <Route path="content/json" element={<AdminContentJsonPage />} />
        <Route path="content/new" element={<AdminPageEditPage />} />
        <Route path="content/:id" element={<AdminPageEditPage />} />
        <Route path="content" element={<AdminContentPage />} />
        <Route path="live" element={<AdminEventLegacyRedirect target="stream" />} />
        <Route path="replays" element={<AdminEventLegacyRedirect target="stream" />} />
        <Route path="youtube" element={<AdminYoutubeLibraryPage />} />
        <Route path="comments" element={<AdminCommentsPage />} />
        <Route path="settings" element={<AdminSettingsLayout />}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path="general" element={<AdminSettingsGeneralPage />} />
          <Route path="contacts" element={<AdminSettingsContactsPage />} />
          <Route path="social" element={<AdminSettingsSocialPage />} />
          <Route path="footer" element={<AdminSettingsFooterPage />} />
          <Route path="email" element={<AdminSettingsEmailPage />} />
          <Route path="sms" element={<AdminSettingsSmsPage />} />
          <Route path="address-book" element={<AdminSettingsAddressBookPage />} />
          <Route path="youtube" element={<AdminSettingsYoutubePage />} />
          <Route path="users" element={<AdminSettingsUsersPage />} />
        </Route>
        <Route path="site" element={<Navigate to="/admin/settings/general" replace />} />
      </Route>
    </Routes>
  );
}

function ComingSoonGate() {
  const { ready, progress, eventPayload } = useComingSoonBoot();

  if (!ready) {
    return <BootSplash progress={progress} />;
  }

  return <ComingSoonPage eventPayload={eventPayload} />;
}
